import { ProximodistalSequenceGraph } from '@/pages/ReportV2/components/Graphs';
import { IonGrid, IonRow, IonCol, IonIcon, IonButton, IonButtons } from '@ionic/react';
import { closeCircleOutline } from 'ionicons/icons';
import React, { useEffect, useMemo, useState } from 'react';

import { Loading } from '@/components/Loading/Loading';

import { uuid } from '@/utils';

import {
  KinematicData,
  ProximoDistalSequenceAnnotations,
  SingleSideVariabilityGraphAnnotations,
} from '@/models/reportV2.model';

import styles from '@/pages/ReportV2/components/Modals/ModalContent.module.css';

type ProximodistalSequenceModalContentProps = {
  EuclideanData: KinematicData;
  proximodistalSequenceData: ProximoDistalSequenceAnnotations;
  xAxisLeft: number;
  xAxisRight: number;
  setShowZoomModal: any;
  cycleEventAnnotations_Left: SingleSideVariabilityGraphAnnotations[];
  cycleEventAnnotations_Right: SingleSideVariabilityGraphAnnotations[];
};

function createLegend(): any[] {
  const meanEntry = [
    {
      id: uuid(),
      name: 'Mean',
      color: '#FFC700',
    },
  ];

  const allStridesEntry = [
    {
      id: uuid(),
      name: 'All strides',
      color: '#0A2E2C',
    },
  ];

  return [...meanEntry, ...allStridesEntry];
}

export const ProximodistalSequenceModalContent = ({
  EuclideanData,
  proximodistalSequenceData,
  setShowZoomModal,
  xAxisLeft,
  xAxisRight,
  cycleEventAnnotations_Left,
  cycleEventAnnotations_Right,
}: ProximodistalSequenceModalContentProps) => {
  const [loading, setLoading] = useState(true);
  const [legend, setLegend] = useState<any[]>([]);
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);
  const [activeTabPDS, setActiveTabPDS] = useState('Mean');

  const seriesProximodistalSequence_Left = useMemo(() => {
    return showAllStrides ? EuclideanData.allStrides.Left.series : EuclideanData.mean.Left.series;
  }, [showAllStrides, EuclideanData]);

  const seriesProximodistalSequence_Right = useMemo(() => {
    return showAllStrides ? EuclideanData.allStrides.Right.series : EuclideanData.mean.Right.series;
  }, [showAllStrides, EuclideanData]);

  const yAxis_Left = useMemo(() => {
    return showAllStrides ? EuclideanData.allStrides.Left.yAxis : EuclideanData.mean.Left.yAxis;
  }, [showAllStrides, EuclideanData]);

  const yAxis_Right = useMemo(() => {
    return showAllStrides ? EuclideanData.allStrides.Right.yAxis : EuclideanData.mean.Right.yAxis;
  }, [showAllStrides, EuclideanData]);

  // Tab change set the active tab and the data for the graphs.
  const tabChange = (name: string) => {
    localStorage.setItem('activeTabPDS', name);
    setActiveTabPDS(name);
    if (name === 'All strides') {
      setShowAllStrides(true);
      //   setDataLeft(data.kneePositionsLeft);
      //   setDataRight(data.kneePositionsRight);
    } else {
      setShowAllStrides(false);
      //   setDataLeft(data.kneePositionLeft);
      //   setDataRight(data.kneePositionRight);
    }
  };

  useEffect(() => {
    const activeTabPDS = localStorage.getItem('activeTabPDS');
    if (activeTabPDS) {
      setActiveTabPDS(activeTabPDS);
    } else {
      localStorage.setItem('activeTabPDS', 'Mean');
      setActiveTabPDS('Mean');
    }
  });

  useEffect(() => {
    setLegend(createLegend());
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [EuclideanData]);

  const cleanUp = () => {
    localStorage.setItem('activeTabPDS', 'Mean');
    setShowAllStrides(true);
    setShowZoomModal({ show: false, graph: '' });
  };

  return (
    <>
      <IonGrid className={styles.Modalcontent_container} fixed>
        <IonRow className={styles.variabilityLegend_Row}>
          <IonCol className='' size='11'>
            <div className={styles.variabilityLegend}>
              {legend.map((element, _) => {
                const color = element.color;
                const iconStyle = {
                  backgroundColor: color,
                };

                return (
                  <div
                    className={
                      activeTabPDS === element.name
                        ? styles.variabilityLegend_Entry_Selected
                        : styles.variabilityLegend_Entry
                    }
                    key={element.id}
                    onClick={() => {
                      tabChange(element.name);
                    }}>
                    <div className={styles.legend_dot} style={iconStyle} />
                    <p className={styles.legend_name}>{element.name}</p>
                  </div>
                );
              })}
            </div>
          </IonCol>
          <IonCol className='ion-align-items-center' size='1'>
            <IonButtons className='ion-justify-content-center ion-align-self-center' style={{ height: '100%' }}>
              <IonButton className={styles.closeButton} fill='clear' onClick={() => cleanUp()} slot='icon-only'>
                <IonIcon icon={closeCircleOutline} />
              </IonButton>
            </IonButtons>
          </IonCol>
        </IonRow>
        <IonRow className='ion-align-items-center ion-align-items-stretch ion-padding-vertical ion-margin-vertical ion-justify-content-center'>
          {loading && <Loading />}
          {!loading && (
            <>
              <IonCol className='ion-no-padding' size='6'>
                <ProximodistalSequenceGraph
                  cycleEventAnnotations={cycleEventAnnotations_Left}
                  data={seriesProximodistalSequence_Left}
                  graphAnnotations={proximodistalSequenceData.Left}
                  title={'Left'}
                  xAxisStart={xAxisLeft}
                  yAxis={yAxis_Left}
                />
              </IonCol>
              <IonCol className='ion-no-padding' size='6'>
                <ProximodistalSequenceGraph
                  cycleEventAnnotations={cycleEventAnnotations_Right}
                  data={seriesProximodistalSequence_Right}
                  graphAnnotations={proximodistalSequenceData.Right}
                  title={'Right'}
                  xAxisStart={xAxisRight}
                  yAxis={yAxis_Right}
                />
              </IonCol>
            </>
          )}
        </IonRow>
      </IonGrid>
    </>
  );
};
